import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React, { useState } from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { BaseBox } from '@components/common/box/base/BaseBox';
import { breakpoints, mq } from '@styles/breakpoints';
import { getImageUrl } from '@util/images';
import { ImageGalleryModal } from '@components/common';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourMapProps {
  className?: 'string';
  theme?: Theme;
  testId?: string;
  tourMapUrl: string;
  tourName: string;
  isExtension?: boolean;
}

enum MapLegendTransportTypes {
  TRAIN = 'tour-map__legend--train',
  COACH = 'tour-map__legend--coach',
  BOAT = 'tour-map__legend--boat',
  AIRPLANE = 'tour-map__legend--airplane',
  WALKING = 'tour-map__legend--walking'
}

/* ***************** *
 *       Styles      *
 * ***************** */

const Container = styled(BaseBox)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  flex: '1 0 auto',
  padding: '0',
  boxShadow: 'none',
  [mq.small]: {
    flexDirection: 'row'
  },
  ['&:hover']: {
    boxShadow: 'none'
  }
}));

const MapImageWrapper = styled.div({
  width: '100%',
  [mq.small]: {
    width: '66.66%',
    height: 'auto'
  }
});

const MapImage: any = styled.img({
  width: '100%',
  cursor: 'zoom-in',
  borderRadius: '10px 10px 0 0',
  [mq.small]: {
    width: '100%',
    height: '100%',
    borderRadius: '10px 0 0 10px'
  }
});

const MapLegendContainer = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
  padding: '10px 20px',
  [mq.small]: {
    paddingLeft: theme.custom.spacing.xLarge,
    width: '33.33%'
  }
}));

const MapLegendItem = styled.div({
  display: 'flex',
  alignItems: 'center',
  paddingBottom: 10,
  fontSize: '1.6rem',
  width: '50%',
  [mq.small]: {
    width: '100%'
  },
  ['&:last-of-type']: {
    marginRight: 0
  }
});

interface ContainerProps extends ThemeProps {
  color: string;
}

const MapLegendColorLine: any = styled.div(({ theme, color }: ContainerProps) => ({
  width: 40,
  height: 4,
  borderRadius: 2,
  backgroundColor: color,
  marginRight: theme.custom.spacing.large
}));

const NoOfNightsCircle = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: theme.custom.spacing.medium,
  height: theme.custom.spacing.medium,
  backgroundColor: theme.custom.colors.group2.base,
  borderRadius: theme.custom.spacing.medium / 2,
  fontSize: '1.2rem',
  color: theme.custom.colors.white
}));

const MainTourMapLabel = styled.p(({ theme }: ThemeProps) => ({
  display: 'block',
  width: '100%',
  ...(theme.custom.typography.paragraph as any),
  margin: 0
}));

const NoOfNightsContainer = styled.div(({ theme }: ThemeProps) => ({
  width: 40,
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.custom.spacing.large,
  justifyContent: 'center'
}));

const Nights = styled.span();

const MapLegendItems = styled.div(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'baseline',
  width: '100%'
}));

const MapKey = styled.h4(({ theme }: ThemeProps) => ({
  width: '100%',
  margin: `${theme.custom.spacing.large}px 0`,
  [mq.small]: {
    margin: '0',
    marginBottom: '15px',
    paddingTop: '20px'
  }
}));
/* ********************** *
 *    TourMapComponent    *
 * ********************** */

export const TourMapComponent = (props: TourMapProps) => {
  const t: any = useI18NextContext();
  const { tourMapUrl, theme, className, isExtension, tourName } = props;
  const [showMapGallery, setShowMapGallery] = useState<boolean>(false);

  if (!tourMapUrl) {
    return null;
  }

  const legendColors = {
    [MapLegendTransportTypes.TRAIN]: theme!.custom.colors.group5.base,
    [MapLegendTransportTypes.COACH]: theme!.custom.colors.group4.base,
    [MapLegendTransportTypes.BOAT]: theme!.custom.colors.group7.base,
    [MapLegendTransportTypes.AIRPLANE]: theme!.custom.colors.group11.base,
    [MapLegendTransportTypes.WALKING]: theme!.custom.colors.group8.base
  };

  /* *** RENDERERS *** */
  const renderMapLegendItems = () =>
    Object.entries(legendColors).map(([key, color]: [string, string]) => (
      <MapLegendItem key={key}>
        <MapLegendColorLine color={color} />
        <span>{t(key)}</span>
      </MapLegendItem>
    ));

  const galleryImages = (url: string) => [
    {
      thumbnail: getImageUrl(url, breakpoints.small),
      original: getImageUrl(url, breakpoints.medium)
    }
  ];

  const toggleMapGalleryModal = (show?: boolean) => {
    setShowMapGallery(typeof show !== 'undefined' ? show : !showMapGallery);
  };

  return (
    <Container className={className} testId={props.testId || TestId.tourMap.main}>
      <MapImageWrapper onClick={() => toggleMapGalleryModal(true)}>
        <MapImage
          alt={`${tourName} Map`}
          src={`${getImageUrl(tourMapUrl, breakpoints.small)}`}
          srcSet={`${getImageUrl(tourMapUrl, breakpoints.small)} ${breakpoints.small}w, ${getImageUrl(
            tourMapUrl,
            breakpoints.medium
          )} ${breakpoints.medium}w`}
        />
      </MapImageWrapper>
      <MapLegendContainer>
        <MapLegendItems>
          <MapKey>{t('tour-map__map--key')}</MapKey>
          {renderMapLegendItems()}
          <MapLegendItem>
            <NoOfNightsContainer>
              <NoOfNightsCircle>1</NoOfNightsCircle>
            </NoOfNightsContainer>
            <Nights>{t('tour-map__legend--nights')}</Nights>
          </MapLegendItem>
        </MapLegendItems>
        {isExtension && <MainTourMapLabel>{t('tour-map__legend--main-tour-map')}</MainTourMapLabel>}
      </MapLegendContainer>
      <ImageGalleryModal
        galleryImages={galleryImages(tourMapUrl)}
        onDismiss={() => {
          setShowMapGallery(false);
        }}
        showGallery={showMapGallery}
      />
    </Container>
  );
};

export const TourMap = withTheme(TourMapComponent);
